import { LocalizationString } from '@celito.clients/assets';
import { DocumentDownloadActionEnum } from '@celito.clients/enums';
import { files, getDocumentData } from '@celito.clients/services';
import { DocData } from '@celito.clients/types';
import { errorToast } from '@celito.clients/utils';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FileDataProps } from '../multi-file-upload.model';
import { MultiFileViewProps } from './multi-file-view.model';
import MultiFileViewView from './multi-file-view.view';

/**
 * Controller component for MultiFileView that handles file download operations.
 *
 * This component manages the download functionality for attachment files,
 * supporting both server-side documents and client-side (not yet uploaded) files.
 *
 * HEADER/FOOTER FEATURE:
 * This controller is responsible for passing the 'attributeName' to the backend API
 * when downloading attachments. This enables the backend to apply header/footer
 * watermarks to PDF attachments if configured in the attribute's pdfConversionOptions.
 */
export const MultiFileViewController = (props: MultiFileViewProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const { getValues } = useFormContext() ?? {};

  // Extract attributeName from props - this is crucial for header/footer feature
  const { fileNames, objectName, attributeName } = props;

  /**
   * Handles downloading a file that has already been uploaded to the server.
   *
   * @param fileName - The name of the file to download
   *
   * IMPORTANT: The 'attrName' parameter passed to getDocumentData() is essential
   * for the header/footer feature. Without it, the backend cannot:
   * 1. Identify that this download is for an attachment (not the main document)
   * 2. Look up the attribute's pdfConversionOptions configuration
   * 3. Apply header/footer watermarks to PDF files
   */
  const handleDownload = (fileName: string) => async () => {
    try {
      const values = fileNames;
      // Use the provided attributeName, or default to 'attachments__a'
      // This attribute name identifies which document field these files belong to
      const attrName = attributeName || 'attachments__a';

      // Get the record name from the form context using the attribute name
      const [, recordName] = getValues([attrName, 'name__s']);

      const targetDoc = values?.find?.((doc: File | DocData) =>
        doc instanceof File ? doc.name === fileName : doc.label === fileName
      );

      if (targetDoc instanceof File) {
        // File exists only on client-side (not uploaded yet) - download directly
        files.downloadFileBuffer({
          data: targetDoc,
          label: targetDoc.name,
        });
      } else {
        // File is on the server - fetch it via API
        if (!targetDoc.documentId) throw new Error("Can't download file");
        files.downloadFile(
          getDocumentData(
            targetDoc.documentId,
            objectName,
            recordName,
            undefined,
            // Using DownloadRaw action for attachments - the backend will still
            // apply header/footer if includeHeaderAndFooter is enabled for this attribute
            DocumentDownloadActionEnum.DownloadRaw,
            undefined,
            // CRITICAL: Pass the attribute name so backend can identify this as an
            // attachment and apply header/footer watermarks if configured.
            // Without this parameter, header/footer will NOT be applied.
            attrName
          ),
          (fileDocument) => {
            if (!fileDocument) throw new Error("Can't download file");
            files.downloadFileBuffer({
              // Type assertion needed because fileBuffer.data is Uint8Array<ArrayBufferLike>
              // but downloadFileBuffer expects ArrayBuffer | BlobPart. The underlying data
              // is compatible at runtime, this is just a TypeScript strict mode issue.
              data: fileDocument.fileBuffer.data as unknown as ArrayBuffer,
              label: fileDocument.label,
              mimeType: fileDocument.mimeType,
            });
          }
        );
      }
    } catch (error) {
      errorToast({
        message: LocalizationString.THERE_WAS_SOME_PROBLEM_DOWNLOADING_THE_FILE,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles downloading a file that exists only on the client-side (not yet uploaded).
   * This is used for files that the user has selected but hasn't saved yet.
   *
   * @param idx - The index of the file in the fileNames array
   *
   * The function checks both application attribute (__a suffix) and system attribute
   * (__s suffix) to find the file data, as form data can be stored under either key
   * depending on the form mode and data source.
   */
  const handleClientDownload = useCallback(
    (idx: number) => {
      // Use provided attributeName or default to 'attachments__a'
      const attrName = attributeName || 'attachments__a';

      // Derive the system attribute name by replacing __a with __s suffix
      // In Celito's naming convention:
      // - __a suffix = Application/Custom attributes (user-defined)
      // - __s suffix = System attributes (system-managed)
      // Form data might be stored under either key depending on context
      const systemAttrName = attrName.replace('__a', '__s');

      files.downloadFileBuffer({
        // Try to get file from application attribute first, fall back to system attribute
        data: getValues(attrName)[idx] ?? getValues(systemAttrName)[idx],
        label: fileNames[idx],
      });
    },
    [getValues, fileNames, attributeName]
  );

  return (
    <MultiFileViewView
      {...props}
      handleClientDownload={handleClientDownload}
      handleDownload={handleDownload}
      showDownloadBtn={true}
      fileNames={(() => {
        const value = fileNames;
        if (!value) return [];
        const fileArray = value as unknown as (File | FileDataProps)[];
        return fileArray.map((file) =>
          file instanceof File ? file?.name : file?.label
        );
      })()}
      isLoading={loading}
    />
  );
};
